"use client";

import { type AudioSampleTranscript, fetchAudioSampleTranscript } from "@/app/_api/fetchAudioSampleTranscript";
import { WordHighlighter } from "@/app/_ui/components/demos/TTS/WordHighlighter";
import SliderGeneric from "@ui/components/composers/SliderGenericWrapper";
import { AspectRatio } from "@ui/components/core/AspectRatio";
import { Button } from "@ui/components/core/Button";
import { Image } from "@ui/components/core/Image";
import { Noise } from "@ui/components/core/Noise/Noise";
import { Skeleton } from "@ui/components/core/Skeleton";
import { ImageSizes } from "@ui/lib/image";
import { useInView } from "@ui/lib/useInView";
import { logger } from "@utilities/logger";
import { cn } from "@ui/lib/utils";
import { Howl } from "howler";
import { Play } from "lucide-react";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import type {
  AudioPlayerRef,
  BlockAudioPlayerWithTranscriptProps,
  BlockAudioPlayerWithTranscriptSliderProps,
  ContentSlice,
  PreparedSliderItem,
} from "./types";

import { StaticTranscriptRenderer } from "./StaticTranscriptRenderer";
import { WordRenderer } from "./WordRenderer";

// Extended media element interface that supports timeupdate events for WordHighlighter
interface ExtendedMediaElement {
  addEventListener: (event: string, handler: () => void) => void;
  currentTime: number;
  duration: number;
  paused: boolean;
  removeEventListener: (event: string, handler: () => void) => void;
}

export const BlockAudioPlayerWithTranscriptLoader = forwardRef<AudioPlayerRef, BlockAudioPlayerWithTranscriptProps>(
  (props, ref) => {
    const [lazyTranscript, setLazyTranscript] = useState<AudioSampleTranscript | null>(null);
    const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
    const [hasTriedLoadingTranscript, setHasTriedLoadingTranscript] = useState(false);
    const { enableLazyLoading, sampleId, parsedContent } = props;

    // Merge lazy-loaded transcript with existing content
    const mergedContent = useMemo(() => {
      if (!lazyTranscript?.parsedContent || lazyTranscript.parsedContent.length === 0) {
        return parsedContent;
      }

      // If we have existing parsedContent, use it; otherwise use lazy-loaded content
      return (parsedContent?.length || 0) > 0 ? parsedContent : lazyTranscript.parsedContent;
    }, [parsedContent, lazyTranscript]);

    // Lazy-load transcript when playback starts (only if enabled)
    const loadTranscriptLazily = useCallback(async () => {
      if (
        !enableLazyLoading ||
        !sampleId ||
        isLoadingTranscript ||
        !!lazyTranscript?.parsedContent?.length ||
        hasTriedLoadingTranscript
      ) {
        return;
      }

      setIsLoadingTranscript(true);
      setHasTriedLoadingTranscript(true);
      try {
        const transcript = await fetchAudioSampleTranscript(sampleId);
        setLazyTranscript(transcript);
        return transcript;
      } catch (error) {
        // Silently fail for now - could be logged to a monitoring service
      } finally {
        setIsLoadingTranscript(false);
      }
    }, [enableLazyLoading, sampleId, isLoadingTranscript, lazyTranscript, hasTriedLoadingTranscript]);

    return (
      <BlockAudioPlayerWithTranscript
        {...props}
        isLoadingTranscript={isLoadingTranscript}
        onVisibleChange={loadTranscriptLazily}
        parsedContent={mergedContent}
        ref={ref}
      />
    );
  },
);

BlockAudioPlayerWithTranscriptLoader.displayName = "BlockAudioPlayerWithTranscriptLoader";

export const BlockAudioPlayerWithTranscript = forwardRef<AudioPlayerRef, BlockAudioPlayerWithTranscriptProps>(
  (
    {
      audioUrl,
      backgroundImage,
      buttonStyle = "default",
      compact,
      fadeUnspokenWords,
      iconNode,
      isLoadingTranscript,
      onCurrentTimeChange,
      onDurationChange,
      onLoadedChange,
      onPlayingChange,
      onVisibleChange,
      parsedContent,
      className,
      showMetadata,
      showPlayButton = false,
      subtitle,
      title,
    },
    ref,
  ) => {
    const howlRef = useRef<Howl | null>(null);
    const mediaElementRef = useRef<ExtendedMediaElement | null>(null);
    const animationFrameRef = useRef<null | number>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [_currentTime, setCurrentTime] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const tG = useTranslations("ClientKeys.Globals");
    const inViewRef = useRef<HTMLDivElement>(null);
    const inView = useInView(inViewRef, { once: true });

    // Check if content is overflowing
    const checkOverflow = useCallback(() => {
      if (!scrollContainerRef.current) return;

      const container = scrollContainerRef.current;
      const hasOverflow = container.scrollHeight > container.clientHeight;
      setIsOverflowing(hasOverflow);
    }, []);

    // Handle user scroll detection
    const handleScroll = useCallback(() => {
      // Set user scrolling flag
      setIsUserScrolling(true);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set timeout to re-enable autoscrolling after user stops scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      }, 1500); // Re-enable after 1.5 seconds of no scrolling
    }, []);

    // Function to scroll to highlighted word
    const scrollToElement = useCallback(
      (element: HTMLElement) => {
        if (!scrollContainerRef.current || !isPlaying || isUserScrolling) return;

        const container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // Calculate if element is outside the visible area
        const isAboveView = elementRect.top < containerRect.top;
        const isBelowView = elementRect.bottom > containerRect.bottom;

        if (isAboveView || isBelowView) {
          // Calculate the target scroll position to center the element
          const containerHeight = containerRect.height;
          const elementTop = element.offsetTop;
          const elementHeight = element.offsetHeight;

          // Center the element in the container
          const targetScrollTop = elementTop - containerHeight / 2 + elementHeight / 2;

          // Smooth scroll to the target position
          container.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: "smooth",
          });
        }
      },
      [isPlaying, isUserScrolling],
    );

    // Set up scroll event listener
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      // Initial overflow check
      checkOverflow();

      // Set up scroll listener
      container.addEventListener("scroll", handleScroll, { passive: true });

      // Set up ResizeObserver to detect content changes
      const resizeObserver = new ResizeObserver(() => {
        checkOverflow();
      });
      resizeObserver.observe(container);

      // Also observe window resize
      const handleWindowResize = () => checkOverflow();
      window.addEventListener("resize", handleWindowResize);

      return () => {
        container.removeEventListener("scroll", handleScroll);
        resizeObserver.disconnect();
        window.removeEventListener("resize", handleWindowResize);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }, [handleScroll, checkOverflow, parsedContent]);

    // Check overflow when content changes
    useEffect(() => {
      setTimeout(() => {
        checkOverflow();
      }, 500);
    }, [parsedContent, checkOverflow]);

    useEffect(() => {
      if (inView) {
        onVisibleChange?.(true);
      }
    }, [inView, onVisibleChange]);

    // Progress tracking with animation frame
    const startProgressTracking = useCallback(() => {
      const updateProgress = () => {
        if (howlRef.current && howlRef.current.playing()) {
          const time = howlRef.current.seek() || 0;
          setCurrentTime(time);
          onCurrentTimeChange?.(time);
          animationFrameRef.current = requestAnimationFrame(updateProgress);
        }
      };
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    }, [onCurrentTimeChange]);

    const stopProgressTracking = useCallback(() => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }, []);

    // Create Howl instance on demand
    const createHowlInstance = useCallback(() => {
      if (howlRef.current) {
        return howlRef.current;
      }

      setIsLoading(true);

      const howl = new Howl({
        src: [audioUrl],
        html5: true, // Use HTML5 audio for streaming
        preload: false, // Don't preload anything
        onload: () => {
          setIsLoaded(true);
          setIsLoading(false);
          onLoadedChange?.(true);
          onDurationChange?.(howl.duration());
        },
        onplay: () => {
          setIsPlaying(true);
          onPlayingChange?.(true);
          startProgressTracking();
        },
        onpause: () => {
          setIsPlaying(false);
          onPlayingChange?.(false);
          stopProgressTracking();
        },
        onstop: () => {
          setIsPlaying(false);
          onPlayingChange?.(false);
          stopProgressTracking();
        },
        onend: () => {
          setIsPlaying(false);
          setCurrentTime(0);
          onPlayingChange?.(false);
          onCurrentTimeChange?.(0);
          stopProgressTracking();
        },
        onloaderror: (id, error) => {
          logger.error(error instanceof Error ? error : null, "[BlockAudioPlayerWithTranscript] Audio load error", {
            howlId: id,
            errorCode: error,
          });
          setIsLoaded(false);
          setIsLoading(false);
          onLoadedChange?.(false);
        },
        onplayerror: (id, error) => {
          logger.error(error instanceof Error ? error : null, "[BlockAudioPlayerWithTranscript] Audio play error", {
            howlId: id,
            errorCode: error,
          });
          setIsPlaying(false);
          setIsLoading(false);
          onPlayingChange?.(false);
          stopProgressTracking();
        },
      });

      howlRef.current = howl;

      // Create a simple media element wrapper that WordHighlighter can use
      mediaElementRef.current = {
        get currentTime() {
          return howl.seek() || 0;
        },
        get duration() {
          return howl.duration() || 0;
        },
        get paused() {
          return !howl.playing();
        },
        addEventListener: () => {
          // WordHighlighter will use its own animation frame for timeupdate
          // No need to handle events here since we're tracking time separately
        },
        removeEventListener: () => {
          // No-op since we're not actually using event listeners
        },
      };

      return howl;
    }, [
      audioUrl,
      onLoadedChange,
      onDurationChange,
      onPlayingChange,
      onCurrentTimeChange,
      startProgressTracking,
      stopProgressTracking,
    ]);

    // Clean up on audioUrl change or unmount
    useEffect(() => {
      // Clean up existing instance when audioUrl changes
      if (howlRef.current) {
        howlRef.current.unload();
        howlRef.current = null;
      }
      if (mediaElementRef.current) {
        mediaElementRef.current = null;
      }
      stopProgressTracking();

      // Reset states when audioUrl changes
      setIsLoaded(false);
      setIsLoading(false);
      setIsPlaying(false);
      setCurrentTime(0);

      return () => {
        if (howlRef.current) {
          howlRef.current.unload();
          howlRef.current = null;
        }
        if (mediaElementRef.current) {
          mediaElementRef.current = null;
        }
        stopProgressTracking();
      };
    }, [audioUrl, stopProgressTracking]);

    // Audio controls
    const togglePlayPause = useCallback(() => {
      // Create Howl instance on first play if it doesn't exist
      if (!howlRef.current) {
        const howl = createHowlInstance();
        // Start loading and playing immediately
        howl.load();
        howl.play();
        return;
      }

      if (!isLoaded) {
        // If not loaded yet but instance exists, try to load and play
        howlRef.current.load();
        howlRef.current.play();
        return;
      }

      if (isPlaying) {
        howlRef.current.pause();
      } else {
        howlRef.current.play();
      }
    }, [isPlaying, isLoaded, createHowlInstance]);

    // Handle container click for play/pause
    const handleContainerClick = useCallback(() => {
      togglePlayPause();
    }, [togglePlayPause]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          togglePlayPause();
        }
      },
      [togglePlayPause],
    );

    useImperativeHandle(
      ref,
      () => ({
        play: () => {
          if (isPlaying) return;

          if (!howlRef.current) {
            const howl = createHowlInstance();
            howl.load();
            howl.play();
            return;
          }

          if (!isLoaded) {
            howlRef.current.load();
            howlRef.current.play();
            return;
          }

          howlRef.current.play();
        },
        pause: () => {
          if (!howlRef.current || !isLoaded || !isPlaying) return;
          howlRef.current.pause();
        },
        togglePlayPause,
        seekTo: (time: number) => {
          if (howlRef.current) {
            howlRef.current.seek(time);
          }
        },
        getCurrentTime: () => {
          return howlRef.current?.seek() || 0;
        },
        getDuration: () => {
          return howlRef.current?.duration() || 0;
        },
        isPlaying: () => {
          return isPlaying;
        },
        isLoaded: () => {
          return isLoaded;
        },
      }),
      [isLoaded, isPlaying, togglePlayPause, createHowlInstance],
    );

    return (
      <div
        className={cn(
          "relative flex h-full w-full cursor-pointer flex-col items-center rounded-xl",
          showMetadata ? "justify-start" : "justify-center",
          compact ? "p-24 lg:p-32" : "p-32 pb-52 lg:p-52",
        )}
        onClick={handleContainerClick}
        onKeyDown={handleKeyDown}
        ref={inViewRef}
        role='button'
        tabIndex={0}
      >
        {backgroundImage && (
          <Image
            alt=''
            className='absolute inset-0 h-full w-full object-cover'
            loading='lazy'
            quality={50}
            resource={backgroundImage}
            sizes={ImageSizes.FULL_1_3_ON_LG}
          />
        )}
        {showMetadata && backgroundImage && <Noise className='absolute inset-0' />}
        {/* Metadata Header */}
        {showMetadata && (title || subtitle || iconNode) && (
          <div className='relative flex min-h-76 w-full items-start gap-12'>
            {iconNode && (
              <div className='tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-[10px] tw-bg-white/10 mt-2 flex-shrink-0'>
                {iconNode}
              </div>
            )}
            <div className='flex-1'>
              {title && <p className='f-paragraph-01 font-medium text-inverse'>{title}</p>}
              {subtitle && <p className='f-paragraph-03 line-clamp-2 text-inverse opacity-60'>{subtitle}</p>}
            </div>
          </div>
        )}
        <AnimatePresence mode='wait'>
          {isLoadingTranscript ? (
            <m.div
              className={cn("flex h-full w-full flex-col gap-8 opacity-40", showMetadata && "mb-24 mt-auto md:my-52")}
              exit={{ opacity: 0 }}
            >
              <Skeleton className='h-24 w-full' />
              <Skeleton className='h-24 w-full' />
              <Skeleton className='h-24 w-full' />
              <Skeleton className='h-24 w-4/5' />
            </m.div>
          ) : (
            <m.div
              animate={{ opacity: 1 }}
              className={cn(
                "scrollbar-none relative max-h-full max-w-4xl py-4 text-inverse",
                isPlaying ? "overflow-y-auto" : "overflow-hidden",
                showMetadata && "my-24 md:my-52",
                className,
              )}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              key='transcript'
              ref={scrollContainerRef}
              style={{
                // Gradient mask for smooth fade at top and bottom - only when overflowing
                ...(isOverflowing && {
                  maskImage: isPlaying
                    ? "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)"
                    : "linear-gradient(to bottom, black 0%, black 85%, transparent 100%)",
                  WebkitMaskImage: isPlaying
                    ? "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)"
                    : "linear-gradient(to bottom, black 0%, black 85%, transparent 100%)",
                }),
              }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {/* Transcript Content */}
              {parsedContent && parsedContent.length > 0 ? (
                <div className='space-y-32 pb-32'>
                  {parsedContent?.map((item: ContentSlice, contentIndex: number) => (
                    <div key={item.id || contentIndex}>
                      {/* Speaker Label */}
                      {item.speaker && <div className='f-ui-03 mb-4 opacity-70'>{item.speaker}</div>}

                      {/* Transcript with Word Highlighting */}
                      {item.transcript && (
                        <div
                          className={
                            showMetadata ? "f-paragraph-01" : compact ? "f-description-03" : "f-description-02"
                          }
                        >
                          {item.sttSlice ? (
                            isPlaying ? (
                              <WordHighlighter
                                className='cursor-pointer'
                                displayWords={item.sttSlice.displayWords}
                                endTimes={item.sttSlice.endTimes}
                                isPlaying={isPlaying}
                                mediaElement={mediaElementRef.current as any}
                                renderWord={(word, highlighted, wordIndex, isPast) => (
                                  <WordRenderer
                                    compact={compact}
                                    fadeUnspoken={fadeUnspokenWords}
                                    hasBeenSpoken={isPast}
                                    highlighted={highlighted}
                                    key={wordIndex}
                                    onHighlight={scrollToElement}
                                    showMetadata={showMetadata}
                                    word={word}
                                    wordIndex={wordIndex}
                                  />
                                )}
                                startTimes={item.sttSlice.startTimes}
                                words={item.sttSlice.words}
                              />
                            ) : (
                              <StaticTranscriptRenderer
                                compact={compact}
                                displayWords={item.sttSlice.displayWords}
                                showMetadata={showMetadata}
                              />
                            )
                          ) : (
                            // Fallback if no STT data
                            <div className='f-description-02'>{item.transcript}</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : null}
            </m.div>
          )}
        </AnimatePresence>

        {showPlayButton && (
          <>
            {buttonStyle === "icon" ? (
              <Button
                className={cn(
                  "effect-opacity shadow-none absolute bottom-12 left-12 w-60 border-none bg-overlay-darker text-inverse",
                  isPlaying && "opacity-0",
                )}
                disabled={isLoading}
                size='md'
                variant='elevated'
              >
                <Play className='h-16 w-16' fill='currentColor' />
              </Button>
            ) : (
              <Button
                className={cn(
                  "effect-opacity bg-transparent absolute bottom-12 left-12 flex items-center gap-4 border-inverse text-inverse enabled:hover:border-inverse",
                  isPlaying && "opacity-0",
                )}
                disabled={isLoading}
                size='md'
                variant='elevated'
              >
                {isLoading ? tG("loading") : tG("play")}
              </Button>
            )}
          </>
        )}
      </div>
    );
  },
);

BlockAudioPlayerWithTranscript.displayName = "BlockAudioPlayerWithTranscript";

// Client-side slider component with refs and interactivity
export const BlockAudioPlayerWithTranscriptSlider = ({
  preparedItems,
  cta,
  showMetadata,
  fadeUnspokenWords,
  buttonStyle,
}: BlockAudioPlayerWithTranscriptSliderProps) => {
  const audioRefs = useRef<(AudioPlayerRef | null)[]>([]);

  // Stop all other players when a new one starts
  const stopAllOthers = useCallback((exceptIndex: number) => {
    audioRefs.current.forEach((ref, index) => {
      if (ref && index !== exceptIndex && ref.isPlaying()) {
        ref.pause();
      }
    });
  }, []);

  const aspectRatio = showMetadata ? "3x4" : "1x1";

  const sliderItems = useMemo(() => {
    // Reset refs array to match the number of items
    audioRefs.current = new Array(preparedItems.length).fill(null);

    return preparedItems.map((item, index) => {
      const props: BlockAudioPlayerWithTranscriptProps = {
        audioUrl: item.audioUrl,
        backgroundImage: item.backgroundImage,
        buttonStyle: item.buttonStyle || buttonStyle,
        enableLazyLoading: !!item.sampleId, // Enable lazy loading if sampleId is provided
        fadeUnspokenWords,
        iconNode: item.iconNode,
        sampleId: item.sampleId,
        showMetadata,
        subtitle: item.subtitle,
        title: item.title,
        onPlayingChange: (isPlaying: boolean) => {
          if (isPlaying) {
            stopAllOthers(index);
          }
        },
      };
      return (
        <AspectRatio className='mx-auto' key={item.id} ratio={aspectRatio}>
          <BlockAudioPlayerWithTranscriptLoader
            {...props}
            ref={ref => {
              audioRefs.current[index] = ref;
            }}
            showPlayButton
          />
        </AspectRatio>
      );
    });
  }, [preparedItems, stopAllOthers, showMetadata, fadeUnspokenWords, buttonStyle, aspectRatio]);

  return preparedItems.length >= 3 ? (
    <SliderGeneric alignControls='start' cta={cta} items={sliderItems} itemsPerViewportAtLg={3} />
  ) : (
    <div className='flex flex-col items-center'>
      <div className='flex w-full flex-col items-center justify-center gap-24 md:flex-row'>
        {sliderItems.map((item, index) => (
          <div className='max-w-[400px] flex-1' key={index}>
            {item}
          </div>
        ))}
      </div>
      {cta}
    </div>
  );
};

// Separate component for rotater items
const RotaterItem = ({
  item,
  audioRefs,
  onItemClick,
  position,
}: {
  audioRefs: React.RefObject<Map<string, AudioPlayerRef>>;
  item: PreparedSliderItem;
  onItemClick: (id: string) => void;
  position: "center" | "left" | "right";
}) => {
  const isCenter = position === "center";
  const isLeft = position === "left";
  const isRight = position === "right";

  return (
    <div
      className={cn(
        "absolute left-1/2 top-1/2 aspect-1x1 h-3/5 -translate-y-1/2 cursor-pointer duration-500 ease-in-out md:h-3/5 lg:h-full",
        isCenter && "z-20 -translate-x-1/2 rounded-xl shadow-md",
        isLeft && "z-10 translate-x-[calc(-50%-100px)] scale-75 hover:scale-90",
        isRight && "z-10 translate-x-[calc(-50%+100px)] scale-75 hover:scale-90",
      )}
      key={item.id}
      onClick={() => onItemClick(item.id)}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onItemClick(item.id);
        }
      }}
      role='button'
      tabIndex={0}
    >
      <AspectRatio ratio='1x1'>
        <BlockAudioPlayerWithTranscriptLoader
          audioUrl={item.audioUrl}
          backgroundImage={item.backgroundImage}
          compact
          enableLazyLoading={!!item.sampleId} // Enable lazy loading if sampleId is provided
          parsedContent={item.parsedContent}
          ref={ref => {
            if (ref) {
              audioRefs.current.set(item.id, ref);
            }
          }}
          sampleId={item.sampleId}
          showPlayButton
        />
      </AspectRatio>
    </div>
  );
};

// Calculate the visual position for each item based on activeId and rotation direction
const getItemPosition = (
  itemId: string,
  activeId: string,
  previousActiveId: string,
  preparedItems: PreparedSliderItem[],
) => {
  if (preparedItems.length === 2) {
    // For 2 items, just alternate between left and right
    return itemId === activeId ? "center" : "left";
  }

  // For 3 items, find the active item and determine rotation direction
  const activeIndex = preparedItems.findIndex(item => item.id === activeId);
  const previousIndex = preparedItems.findIndex(item => item.id === previousActiveId);
  const itemIndex = preparedItems.findIndex(item => item.id === itemId);

  if (activeIndex === -1 || itemIndex === -1) return "center";

  // Determine rotation direction
  const isClockwise = (activeIndex - previousIndex + preparedItems.length) % preparedItems.length === 1;

  if (itemId === activeId) {
    return "center";
  } else if (isClockwise) {
    // Clockwise rotation: left becomes center, center becomes right, right becomes left
    if (itemIndex === (activeIndex + 1) % preparedItems.length) {
      return "right";
    } else {
      return "left";
    }
  } else {
    // Counter-clockwise rotation: right becomes center, center becomes left, left becomes right
    if (itemIndex === (activeIndex - 1 + preparedItems.length) % preparedItems.length) {
      return "left";
    } else {
      return "right";
    }
  }
};

export const BlockAudioPlayerWithTranscriptRotater = ({ preparedItems }: { preparedItems: PreparedSliderItem[] }) => {
  // Keep items in their original order, just track which one is active
  const [activeId, setActiveId] = useState(preparedItems[1]?.id);
  const [previousActiveId, setPreviousActiveId] = useState(preparedItems[1]?.id);
  const audioRefs = useRef<Map<string, AudioPlayerRef>>(new Map());
  const [itemPositions, setItemPositions] = useState<Record<string, "center" | "left" | "right">>({});

  // Stop all other players when a new one starts
  const stopAllOthers = useCallback(() => {
    audioRefs.current.forEach(ref => {
      if (ref.isPlaying()) {
        ref.pause();
      }
    });
  }, []);

  useEffect(() => {
    const positions = preparedItems.reduce(
      (acc, item) => {
        acc[item.id] = getItemPosition(item.id, activeId, previousActiveId, preparedItems);
        return acc;
      },
      {} as Record<string, "center" | "left" | "right">,
    );
    setItemPositions(positions);
  }, [preparedItems, activeId, previousActiveId]);

  // Handle item click to rotate to center
  const handleItemClick = useCallback(
    (clickedId: string) => {
      if (clickedId === activeId) return;

      setPreviousActiveId(activeId);
      setActiveId(clickedId);

      // Stop all players and start the clicked one
      stopAllOthers();
    },
    [activeId, stopAllOthers],
  );

  // Don't render if we don't have items or more than 3
  if (preparedItems.length === 0 || preparedItems.length > 3) {
    return null;
  }

  return (
    <div className='relative flex h-full items-center justify-center'>
      {/* Render all items with their calculated positions */}
      {preparedItems.map(item => (
        <RotaterItem
          audioRefs={audioRefs}
          item={item}
          key={item.id}
          onItemClick={handleItemClick}
          position={itemPositions[item.id]}
        />
      ))}
    </div>
  );
};
